import { Injectable, Inject, signal, computed, Signal } from '@angular/core';
import { Observable, Subject, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { Activity } from '@go-modules/interfaces/activity';
import { Task } from '@go-modules/interfaces/task';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';

interface AdditionalSettingsValidator {
	validateAdditionalSettingsForm(): boolean;
}

/**
 * When in edit mode, provides a cloned "working copy" of the activity
 * that components can modify. On save, updates the original. On cancel,
 * discards changes and reverts to the original.
 */
@Injectable({
	providedIn: 'root'
})
export class ActivityEditModeService {
	readonly #isEditMode = signal(false);
	readonly #originalActivity = signal<Activity | null>(null);
	readonly #editedActivity = signal<Activity | null>(null);
	readonly #originalTasks = signal<Task[]>([]);
	readonly #editedTasks = signal<Task[]>([]);
	readonly #hasChanges = signal(false);
	readonly #isSaving = signal(false);
	readonly #changesReverted$ = new Subject<void>();

	#additionalSettingsValidator: AdditionalSettingsValidator | null = null;

	readonly #selectedActivity = toSignal(
		this.selectedService.selectedSubject.asObservable().pipe(
			map((selected) => selected.activity)
		),
		{ initialValue: null as Activity | null }
	);

	public readonly isEditMode: Signal<boolean> = this.#isEditMode.asReadonly();
	public readonly hasUnsavedChanges: Signal<boolean> = this.#hasChanges.asReadonly();
	public readonly isSaving: Signal<boolean> = this.#isSaving.asReadonly();
	public readonly changesReverted$ = this.#changesReverted$.asObservable();

	public readonly currentTasks: Signal<Task[]> = computed(() =>
		this.#isEditMode() ? this.#editedTasks() : this.#originalTasks()
	);

	public readonly currentActivity: Signal<Activity | null> = computed(() =>
		this.#isEditMode() ? this.#editedActivity() : this.#selectedActivity()
	);

	/**
	 * Tasks indexed by ID for efficient lookup
	 * The Map is computed once and cached until currentTasks changes.
	 *
	 * For reactive access, wrap in computed:
	 * readonly task = computed(() => this.editModeService.tasksById().get(this.taskId));
	 *
	 * For one-time access (non-reactive):
	 * const task = this.editModeService.tasksById().get(taskId);
	 *
	 */
	public readonly tasksById: Signal<Map<number, Task>> = computed(() =>
		new Map(this.currentTasks().map((t) => [t.id, t]))
	);

	constructor (
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(activityToken) private activityModel: { model: (activity: Activity) => Activity },
		private dialog: MatDialog,
		private translate: TranslateService
	) {}

	/**
	 * Set the current tasks (should be called when tasks are loaded from the API)
	 * This keeps the service in sync with the latest task data.
	 * Only updates if not in edit mode (don't override the working copy).
	 */
	public setOriginalTasks (tasks: Task[]): void {
		if (!this.#isEditMode()) {
			this.#originalTasks.set(tasks);
		}
	}

	public enterEditMode (tasks: Task[]): void {
		if (this.#isEditMode()) {
			return;
		}

		const activity = this.selectedService.getActivity();
		if (!activity) {
			throw new Error('Cannot enter edit mode: no activity selected');
		}

		this.#originalActivity.set(activity);
		this.#editedActivity.set(structuredClone(activity));
		this.#originalTasks.set(structuredClone(tasks));
		this.#editedTasks.set(structuredClone(tasks));
		this.#isEditMode.set(true);
		this.#hasChanges.set(false);
	}

	/**
	 * Exit edit mode without saving changes
	 */
	public exitEditMode (): void {
		this.#isEditMode.set(false);
		this.#originalActivity.set(null);
		this.#editedActivity.set(null);
		// Don't clear originalTasks - keep them so currentTasks can return them
		// when components re-render after exiting edit mode
		this.#editedTasks.set([]);
		this.#hasChanges.set(false);
		this.#additionalSettingsValidator = null;
	}

	public updateEditedActivity (activity: Activity, options?: { markAsChanged?: boolean }): void {
		if (!this.#isEditMode()) {
			return;
		}

		this.#editedActivity.set(activity);

		if (options?.markAsChanged !== false) {
			this.#hasChanges.set(true);
		}
	}

	/**
	 * Sync the original activity during edit mode (e.g., when server confirms partial save).
	 * Does NOT mark as changed since this reflects server state.
	 */
	public syncOriginalActivity (activity: Activity): void {
		if (!this.#isEditMode()) {
			return;
		}
		this.#originalActivity.set(activity);
	}

	public updateEditedTasks (tasks: Task[]): void {
		if (!this.#isEditMode()) {
			return;
		}

		this.#editedTasks.set(tasks);
		this.#hasChanges.set(true);
	}

	public updateEditedTask (task: Task): void {
		if (!this.#isEditMode()) {
			throw new Error('Cannot update task: not in edit mode');
		}

		const tasks = this.#editedTasks();
		const index = tasks.findIndex((t) => t.id === task.id);

		if (index === -1) {
			throw new Error(`Task with id ${task.id} not found in edited tasks`);
		}

		const updatedTasks = [...tasks];
		updatedTasks[index] = task;
		this.#editedTasks.set(updatedTasks);
		this.#hasChanges.set(true);
	}

	public setIsSaving (isSaving: boolean): void {
		this.#isSaving.set(isSaving);
	}

	public commitChanges (updatedActivity: Activity): void {
		if (updatedActivity) {
			this.selectedService.setActivity(this.activityModel.model(updatedActivity));
		}

		// Update originalTasks with the edited tasks so they persist after exiting edit mode
		// This ensures that components can still access the saved changes via getTask()
		// until the next navigation or edit mode entry
		this.#originalTasks.set(this.#editedTasks());
		this.exitEditMode();
	}

	public revertChanges (): { activity: Activity | null; tasks: Task[] } {
		const originalActivity = this.#originalActivity();
		const originalTasks = this.#originalTasks();

		if (originalActivity) {
			this.selectedService.setActivity(this.activityModel.model(originalActivity));
		}

		this.#changesReverted$.next();
		this.exitEditMode();

		return {
			activity: originalActivity,
			tasks: [...originalTasks]
		};
	}

	public confirmDiscardChanges (): Observable<boolean> {
		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: this.translate.instant('activity-settings_controller-unsaved-changes'),
				message: this.translate.instant('activity-settings_controller-message-2'),
				confirmText: this.translate.instant('common_discard-changes'),
				cancelText: this.translate.instant('common_cancel')
			},
			disableClose: true
		});

		return dialogRef.afterClosed().pipe(
			map((confirmed: boolean) => {
				if (confirmed) {
					this.revertChanges();
				}
				// allow/prevent navigation
				return confirmed;
			})
		);
	}

	public registerAdditionalSettingsValidator (validator: AdditionalSettingsValidator): void {
		this.#additionalSettingsValidator = validator;
	}

	public validateAdditionalSettingsForm (): boolean {
		return this.#additionalSettingsValidator?.validateAdditionalSettingsForm() ?? true;
	}
}
